// call-it-out/src/components/FeedbackToast.jsx
import { useEffect, useRef, useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import "../stylesheets/FeedbackToast.css";

const TOAST_INTERVAL_MS = (1000 * 60) * 10;

const toastOptions = [
    {
        id: "school-request",
        question: "Want to see Call It Out Loud at your school?",
        primaryText: "Yes",
        secondaryText: "Maybe Later",
    },
    {
        id: "helpful-feedback",
        question: "Do you think this could help your school community?",
        primaryText: "Yes",
        secondaryText: "No",
    },
];

const US_STATES = [
    { value: "", label: "Select a state" },
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
    { value: "DC", label: "Washington, DC" },
];

export default function FeedbackToast({ gameState }) {
    const [isVisible, setIsVisible] = useState(false);
    const [toastIndex, setToastIndex] = useState(0);
    const [showSchoolModal, setShowSchoolModal] = useState(false);
    const [schoolName, setSchoolName] = useState("");
    const [schoolState, setSchoolState] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const timerRef = useRef(null);

    const currentToast = toastOptions[toastIndex];

    const gameId = gameState?.gameId || gameState?.id || null;

    const scheduleNextToast = () => {
        window.clearTimeout(timerRef.current);

        timerRef.current = window.setTimeout(() => {
            setIsVisible(true);
        }, TOAST_INTERVAL_MS);
    };

    const hideAndScheduleNext = () => {
        setIsVisible(false);
        setToastIndex((currentIndex) => (currentIndex + 1) % toastOptions.length);
        scheduleNextToast();
    };

    useEffect(() => {
        scheduleNextToast();

        return () => {
            window.clearTimeout(timerRef.current);
        };
    }, []);

    const saveFeedback = async (data) => {
        await addDoc(collection(db, "callItOutFeedback"), {
            ...data,
            gameId,
            pageUrl: window.location.href,
            userAgent: navigator.userAgent,
            createdAt: serverTimestamp(),
        });
    };

    const handlePrimaryClick = async () => {
        setSubmitError("");

        if (currentToast.id === "school-request") {
            setShowSchoolModal(true);
            return;
        }

        if (currentToast.id === "helpful-feedback") {
            try {
                setIsSubmitting(true);

                await saveFeedback({
                    type: "helpful-feedback",
                    answer: "yes",
                });

                hideAndScheduleNext();
            } catch (error) {
                console.error("Failed to save helpful feedback:", error);
                setSubmitError("Could not save your answer. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleSecondaryClick = async () => {
        setSubmitError("");

        if (currentToast.id === "school-request") {
            hideAndScheduleNext();
            return;
        }

        if (currentToast.id === "helpful-feedback") {
            try {
                setIsSubmitting(true);

                await saveFeedback({
                    type: "helpful-feedback",
                    answer: "no",
                });

                hideAndScheduleNext();
            } catch (error) {
                console.error("Failed to save helpful feedback:", error);
                setSubmitError("Could not save your answer. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleSubmitSchool = async (event) => {
        event.preventDefault();

        const trimmedSchoolName = schoolName.trim();
        const trimmedSchoolState = schoolState.trim();

        if (!trimmedSchoolName) {
            setSubmitError("Please enter a school name.");
            return;
        }

        if (!trimmedSchoolState) {
            setSubmitError("Please enter a state.");
            return;
        }

        try {
            setIsSubmitting(true);
            setSubmitError("");

            await saveFeedback({
                type: "school-request",
                schoolName: trimmedSchoolName,
                schoolState: trimmedSchoolState,
                answer: "yes",
            });

            setSchoolName("");
            setSchoolState("");
            setShowSchoolModal(false);
            hideAndScheduleNext();
        } catch (error) {
            console.error("Failed to save school request:", error);
            setSubmitError("Could not save your school. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {isVisible && (
                <div
                    className="feedback-toast"
                    style={{ "--feedback-toast-bg": "url('/assets/HeroBackgroundBlue.png')" }}
                >
                    <button
                        type="button"
                        className="feedback-toast-close"
                        onClick={hideAndScheduleNext}
                        aria-label="Close feedback prompt"
                    >
                        ×
                    </button>

                    <p className="feedback-toast-question">
                        {currentToast.question}
                    </p>

                    <div className="feedback-toast-actions">
                        <button
                            type="button"
                            className="feedback-toast-button feedback-toast-button-primary"
                            onClick={handlePrimaryClick}
                            disabled={isSubmitting}
                        >
                            {currentToast.primaryText}
                        </button>

                        <button
                            type="button"
                            className="feedback-toast-button feedback-toast-button-secondary"
                            onClick={handleSecondaryClick}
                            disabled={isSubmitting}
                        >
                            {currentToast.secondaryText}
                        </button>
                    </div>

                    {submitError && (
                        <p className="feedback-toast-error">{submitError}</p>
                    )}
                </div>
            )}

            {showSchoolModal && (
                <div className="school-modal-overlay">
                    <div className="school-modal">
                        <button
                            type="button"
                            className="school-modal-close"
                            onClick={() => setShowSchoolModal(false)}
                            aria-label="Close school form"
                        >
                            ×
                        </button>

                        <h2>Bring Call It Out Loud to your school</h2>

                        <p>
                            Tell us your school name, and we may reach out to share that students are interested.
                        </p>

                        <form onSubmit={handleSubmitSchool}>
                            <label htmlFor="school-name">
                                School name
                            </label>

                            <input
                                id="school-name"
                                type="text"
                                value={schoolName}
                                onChange={(event) => setSchoolName(event.target.value)}
                                placeholder="Example: Lincoln High School"
                                maxLength={120}
                            />

                            <label htmlFor="school-state">
                                State
                            </label>

                            <select
                                id="school-state"
                                value={schoolState}
                                onChange={(event) => setSchoolState(event.target.value)}
                            >
                                {US_STATES.map((state) => (
                                    <option key={state.value} value={state.value}>
                                        {state.label}
                                    </option>
                                ))}
                            </select>

                            {submitError && (
                                <p className="school-modal-error">{submitError}</p>
                            )}

                            <button
                                type="submit"
                                className="school-modal-submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Saving..." : "Submit"}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}